import PropTypes from "prop-types"
import React, { useState } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Table from "src/components/Table"

import {
  BILLING_CYCLES,
  INITIAL_DISCOUNT_DISTRIBUTION_OPTIONS,
} from "../../constants"
import DiscountsModal from "../DiscountsModal"
import {
  formatIncomingDiscountData,
  getAmountDisplay,
  getDisplayName,
  parseSelectedDiscount,
} from "../tabPanels/Rate/utils"
import { isMonthlyBilling } from "../utils"

const DiscountTable = ({
  canEdit = true,
  hidePlaceholders = false,
  billingCycle = "",
  contractStartDate = "",
  contractEndDate = "",
  contractCompleted = false,
  featureDisabled = false,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const { currencySymbol } = useMarina()
  const [selectedDiscountIndex, setSelectedDiscountIndex] = useState(undefined)
  const [showDiscountModal, setShowDiscountModal] = useState(false)

  const { fields, append, remove, update } = useFieldArray({
    name: "feesAndDiscounts",
  })
  const addNewDisabled =
    fields.length >= 3 ||
    (isMonthlyBilling({ billingCycle }) && !contractStartDate) ||
    (billingCycle === BILLING_CYCLES.MONTHLY_INSTALLMENTS &&
      !contractEndDate) ||
    featureDisabled

  const onAddDiscount = (data) => {
    const formattedData = formatIncomingDiscountData(data)
    if (selectedDiscountIndex !== undefined) {
      update(selectedDiscountIndex, formattedData)
    } else {
      append(formattedData)
    }
  }

  const onEditClick = (index) => {
    setSelectedDiscountIndex(index)
    setShowDiscountModal(true)
  }

  const onCloseModal = () => {
    setShowDiscountModal(false)
    if (selectedDiscountIndex !== undefined) {
      setSelectedDiscountIndex(undefined)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Form.Label htmlFor="feesAndDiscounts">Discounts</Form.Label>
        <Table autoColumnWidth fullHeight>
          <caption className="sr-only">Discounts Table</caption>
          <Table.Head>
            <Table.Head.Row>
              <Table.Head.Cell columnWidth="60%">Name</Table.Head.Cell>
              <Table.Head.Cell>Amount</Table.Head.Cell>
              {/* edit/delete */}
              <Table.Head.Cell />
            </Table.Head.Row>
          </Table.Head>
          <Table.Body>
            {fields.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={3}>
                  <div className="text-muted w-full text-center">
                    No discounts added
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : null}
            {fields.map((field, i) => {
              return (
                <Table.Row key={field.id}>
                  <Table.Cell>{getDisplayName(field)}</Table.Cell>
                  <Table.Cell>
                    {getAmountDisplay(field, currencySymbol)}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-row justify-end gap-4">
                      {canEdit ? (
                        <a
                          className="cursor-pointer font-semibold"
                          onClick={() => onEditClick(i)}
                        >
                          Edit
                        </a>
                      ) : null}
                      <a
                        className="cursor-pointer font-semibold"
                        onClick={() => remove(i)}
                      >
                        Remove
                      </a>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
      </div>
      {Object.values(errors?.feesAndDiscounts ?? []).map((error, i) => {
        return (
          <Form.Error key={`${i}-${error.message}`}>{error.message}</Form.Error>
        )
      })}
      {fields.length > 0 &&
        billingCycle === BILLING_CYCLES.CUSTOM_INSTALLMENTS && (
          <>
            <Form.Label htmlFor="discount-distribution-option" noMargin>
              Discount payment options
            </Form.Label>
            <p>
              If more than one discount is added, payment options will apply to
              all.
            </p>
            <Form.Select
              id="discount-distribution-option"
              {...register("initialDiscountDistributionOption")}
              hasErrors={Boolean(errors?.initialDiscountDistributionOption)}
            >
              {INITIAL_DISCOUNT_DISTRIBUTION_OPTIONS.map(({ value, text }) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Form.Select>
          </>
        )}
      <div className="flex justify-center">
        <Button
          variant="secondary"
          onClick={() => setShowDiscountModal(true)}
          disabled={addNewDisabled}
        >
          Add Discount
        </Button>
      </div>
      {showDiscountModal ? (
        <DiscountsModal
          onSave={onAddDiscount}
          hidePlaceholders={hidePlaceholders}
          isOpen
          onClose={onCloseModal}
          selectedDiscount={parseSelectedDiscount(
            fields[selectedDiscountIndex]
          )}
          billingCycle={billingCycle}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
          contractCompleted={contractCompleted}
        />
      ) : null}
    </div>
  )
}

DiscountTable.propTypes = {
  canEdit: PropTypes.bool,
  hidePlaceholders: PropTypes.bool,
  billingCycle: PropTypes.string,
  contractStartDate: PropTypes.instanceOf(Date),
  contractEndDate: PropTypes.instanceOf(Date),
  contractCompleted: PropTypes.bool,
  featureDisabled: PropTypes.bool,
}

export default DiscountTable
